import currentLocale from 'utils/locale/currentLocale';

const integerFormatter = Intl.NumberFormat(currentLocale, { maximumFractionDigits: 0 });

const twoDigitFormatter = Intl.NumberFormat(currentLocale, {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const byteFormatter = Intl.NumberFormat(currentLocale, {
  unit: 'byte',
  style: 'unit',
  unitDisplay: 'long',
});

/** See https://www.notion.so/teamhologram/Hologram-Writing-Guide-d85fbb8709e64fdc84374b76f937b566?pvs=4#87fd102e1cda4d818e4f986854f55ad1 */
const getUserFriendlyBytes = (
  bytes?: string | number | null,
  style: 'long' | 'short' | 'bytes' = 'short',
  forceFormatting?: 'integer' | 'decimal'
): string | undefined => {
  if (!bytes && bytes !== 0) {
    return undefined;
  }

  const numBytes: number = Number(bytes);

  if (style === 'bytes') {
    return byteFormatter.format(numBytes);
  }

  if (numBytes < 1000) {
    return `${integerFormatter.format(numBytes)} B`;
  }

  let scaledValue: number = 0;
  let unit: string = '';

  // Determine scaled value and unit based on size and style
  if (style === 'long') {
    if (numBytes < 2_000_000) {
      // Less than 2MB
      scaledValue = Math.round(numBytes / 10) / 100; // KB
      unit = 'KB';
    } else if (numBytes < 2_000_000_000) {
      // Less than 2GB
      scaledValue = Math.round(numBytes / 10_000) / 100; // MB
      unit = 'MB';
    } else if (numBytes < 2_000_000_000_000) {
      // Less than 2TB
      scaledValue = Math.round(numBytes / 10_000_000) / 100; // GB
      unit = 'GB';
    } else {
      // 2TB or more
      scaledValue = Math.round(numBytes / 10_000_000_000) / 100; // TB
      unit = 'TB';
    }
  } else if (style === 'short') {
    if (numBytes < 1_000_000) {
      // Less than 1MB
      scaledValue = Math.round(numBytes / 10) / 100; // KB
      unit = 'KB';
    } else if (numBytes < 1_000_000_000) {
      // Less than 1GB
      scaledValue = Math.round(numBytes / 10_000) / 100; // MB
      unit = 'MB';
    } else if (numBytes < 1_000_000_000_000) {
      // Less than 1TB
      scaledValue = Math.round(numBytes / 10_000_000) / 100; // GB
      unit = 'GB';
    } else {
      // 1TB or more
      scaledValue = Math.round(numBytes / 10_000_000_000) / 100; // TB
      unit = 'TB';
    }
  }

  // Set the default formatter
  let formatter: Intl.NumberFormat = twoDigitFormatter;

  // Check if the scaled value is effectively an integer
  if (scaledValue % 1 === 0) {
    formatter = integerFormatter;
  }

  // If forced to be an integer, set the formatter to integerFormatter
  if (forceFormatting === 'integer') {
    formatter = integerFormatter;
  }

  // If forced to be a decimal, set the formatter to twoDigitFormatter
  if (forceFormatting === 'decimal') {
    formatter = twoDigitFormatter;
  }

  // Format the output string
  return `${formatter.format(scaledValue)} ${unit}`;
};
export default getUserFriendlyBytes;
